import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import adminService from '../../services/adminService';
import productService from '../../services/productService';
import styles from './AdminProductForm.module.scss';
import commonStyles from '../AdminOrdersPage/AdminOrdersPage.module.scss';
import MediaLibraryModal from '../../components/common/MediaLibraryModal/MediaLibraryModal';
import { IoTrashOutline, IoArrowBack, IoAddCircleOutline, IoSaveOutline, IoRefreshOutline, IoImageOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = 'https://huongtramcomoc.vn';

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewProduct = !id;
  const quillRef = useRef(null);

  // State Product
  const [product, setProduct] = useState({ 
    id: '', 
    name: '', 
    price: 0, 
    discount_percentage: 0, 
    category_id: '', 
    short_description: '', 
    description: '', 
    stock_quantity: 0,
    is_featured: 0
  });
  
  // Lưu ID gốc để gửi lên backend nếu người dùng sửa ID
  const [originalId, setOriginalId] = useState('');

  const [categories, setCategories] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attributes, setAttributes] = useState([{ key: '', value: '' }]);
  const [selectMode, setSelectMode] = useState('product'); 

  // --- QUILL CONFIG ---
  const imageHandler = useCallback(() => {
    const editor = quillRef.current.getEditor();
    setSelectMode('description');
    setIsModalOpen(true);
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'align': []}],
        ['link', 'image', 'code-block', 'clean']
      ],
      handlers: { image: imageHandler }
    }
  }), [imageHandler]);

  const formats = ['header', 'bold', 'italic', 'underline', 'blockquote', 'list', 'align', 'link', 'image', 'code-block'];
  
  // --- LOAD DATA ---
  useEffect(() => {
    adminService.getCategories().then(res => setCategories(res.data));
    
    if (!isNewProduct) {
      setLoading(true);
      productService.getProductById(id).then(res => {
        const data = res.data;
        setProduct(data);
        setOriginalId(data.id); // Lưu ID gốc
        setProductImages(data.images || []);
        
        let validAttributes = [{ key: '', value: '' }]; 
        if (data.attributes) {
          try {
            const parsed = JSON.parse(data.attributes);
            if (Array.isArray(parsed) && parsed.length > 0) validAttributes = parsed;
          } catch (e) {}
        }
        setAttributes(validAttributes);
        setLoading(false);
      });
    }
  }, [id, isNewProduct]);

  // --- HANDLERS ---
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value) => setProduct(prev => ({ ...prev, description: value }));

  // --- TẠO ID TỰ ĐỘNG ---
  const handleGenerateId = () => {
    const randomCode = "SP" + Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    setProduct(prev => ({ ...prev, id: randomCode }));
  };

  // Tính giá sale realtime
  const salePrice = product.price * (1 - product.discount_percentage / 100);

  // Xử lý Attributes
  const handleAttrChange = (index, field, value) => {
    const newAttrs = [...attributes];
    newAttrs[index][field] = value;
    setAttributes(newAttrs);
  };
  const addAttrRow = () => setAttributes([...attributes, { key: '', value: '' }]);
  const removeAttrRow = (index) => setAttributes(attributes.filter((_, i) => i !== index));

  // --- XỬ LÝ ẢNH ---
  const handleFileSelect = (file) => {
    if (selectMode === 'product') {
      const exists = productImages.some(img => img.media_file_id === file.id);
      if (exists) { toast.warn("Ảnh này đã có!"); return; }

      const newImageObj = { media_file_id: file.id, file_path: file.file_path, is_thumbnail: 0 };

      if (!isNewProduct) {
        // Đang sửa -> Lưu thẳng DB
        adminService.linkImageToProduct(originalId, file.id).then(() => {
            setProductImages(prev => [...prev, newImageObj]);
            toast.success("Đã thêm ảnh!");
        });
      } else {
        // Đang tạo mới -> Lưu tạm vào state
        setProductImages(prev => [...prev, newImageObj]);
      }
    
    } else if (selectMode === 'description') {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      const index = range ? range.index : editor.getLength();
      editor.insertEmbed(index, 'image', `${BACKEND_URL}${file.file_path}`);
      setSelectMode('product');
    }
  };
  
  const handleUnlinkImage = (mediaFileId) => {
    if (isNewProduct) {
        setProductImages(prev => prev.filter(img => img.media_file_id !== mediaFileId));
    } else {
        if (!window.confirm("Gỡ ảnh này?")) return;
        adminService.unlinkImageFromProduct(originalId, mediaFileId)
          .then(() => setProductImages(prev => prev.filter(img => img.media_file_id !== mediaFileId)));
    }
  };

  const handleSetThumbnail = (mediaFileId) => {
     if (!isNewProduct) {
         adminService.setProductThumbnail(originalId, mediaFileId)
          .then(() => {
            setProductImages(prev => prev.map(img => ({ ...img, is_thumbnail: img.media_file_id === mediaFileId ? 1 : 0 })));
            toast.info('Đã đặt ảnh đại diện.');
          });
     } else {
         setProductImages(prev => prev.map(img => ({ ...img, is_thumbnail: img.media_file_id === mediaFileId ? 1 : 0 })));
     }
  };

  // --- SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product.id || product.id.trim() === '') {
        toast.error("Vui lòng nhập hoặc tạo Mã sản phẩm (ID).");
        return;
    }
    setLoading(true);

    const validAttributes = attributes.filter(attr => attr.key.trim() && attr.value.trim());
    
    // Nếu đang sửa, gửi thêm old_id để backend biết đường update
    const productData = { 
        ...product, 
        attributes: validAttributes,
        old_id: isNewProduct ? null : originalId 
    };

    try {
      if (isNewProduct) {
        // 1. Tạo SP
        const response = await adminService.createProduct(productData);
        const newId = response.data.new_product_id;

        // 2. Link ảnh (nếu có trong state tạm)
        if (productImages.length > 0) {
            // Dùng Promise.all để link ảnh nhanh hơn
            const linkPromises = productImages.map(img => 
                adminService.linkImageToProduct(newId, img.media_file_id)
            );
            await Promise.all(linkPromises);

            // Tìm ảnh thumbnail để set
            const thumb = productImages.find(img => img.is_thumbnail == 1);
            if (thumb) {
                await adminService.setProductThumbnail(newId, thumb.media_file_id);
            }
        }

        setLoading(false);
        toast.success('Tạo sản phẩm thành công!');
        navigate(`/admin/products/edit/${newId}`); 
      } else {
        await adminService.updateProduct(id, productData);
        // Nếu ID thay đổi, cập nhật lại originalId và URL
        if (product.id !== originalId) {
            setOriginalId(product.id);
            navigate(`/admin/products/edit/${product.id}`, { replace: true });
        }
        setLoading(false);
        toast.success('Cập nhật thành công!');
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.message || "Lỗi hệ thống");
    }
  };

  return (
    <div className={commonStyles.adminPage}>
      <div className={styles.headerBar}>
        <Link to="/admin/products" className={styles.backButton}><IoArrowBack /> Quay lại</Link>
        <h1 className={styles.pageTitle}>{isNewProduct ? 'Thêm Sản Phẩm Mới' : `Cập Nhật: ${product.name}`}</h1>
        <div className={styles.actions}>
            <button onClick={handleSubmit} className={styles.saveBtn} disabled={loading}>
                <IoSaveOutline /> {loading ? 'Đang lưu...' : 'Lưu Sản Phẩm'}
            </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className={styles.productLayout}>
        {/* CỘT TRÁI */}
        <div className={styles.leftColumn}>
            <div className={styles.card}>
                <h3>Thông tin chung</h3>
                
                {/* ID INPUT SECTION */}
                <div className={styles.formGroup}>
                    <label>Mã sản phẩm (ID) <span className={styles.required}>*</span></label>
                    <div className={styles.idInputGroup}>
                        <input 
                            type="text" 
                            name="id" 
                            value={product.id} 
                            onChange={handleTextChange} 
                            placeholder="Nhập mã hoặc tạo tự động" 
                            required
                        />
                        <button type="button" onClick={handleGenerateId} className={styles.genIdBtn} title="Sinh mã tự động">
                            <IoRefreshOutline /> Tạo mã
                        </button>
                    </div>
                    <small className={styles.hint}>Bạn có thể nhập tay hoặc bấm nút để tạo mã tự động.</small>
                </div>

                <div className={styles.formGroup}>
                    <label>Tên sản phẩm <span className={styles.required}>*</span></label>
                    <input type="text" name="name" value={product.name} onChange={handleTextChange} required />
                </div>
                <div className={styles.formGroup}>
                    <label>Mô tả ngắn</label>
                    <textarea name="short_description" rows="3" value={product.short_description || ''} onChange={handleTextChange}></textarea>
                </div>
            </div>

            <div className={styles.card}>
                <h3>Mô tả chi tiết</h3>
                <div className={styles.editorContainer}>
                    <ReactQuill 
                        ref={quillRef} theme="snow" value={product.description || ''} onChange={handleDescriptionChange} modules={modules} formats={formats}
                        placeholder="Nội dung chi tiết..."
                    />
                </div>
            </div>

            <div className={styles.card}>
                <h3>Thông số kỹ thuật</h3>
                <div className={styles.attributesContainer}>
                    {attributes.map((attr, index) => (
                    <div key={index} className={styles.attrRow}>
                        <input type="text" placeholder="Tên (VD: Mùi hương)" value={attr.key} onChange={(e) => handleAttrChange(index, 'key', e.target.value)} />
                        <input type="text" placeholder="Giá trị (VD: Ngọt dịu)" value={attr.value} onChange={(e) => handleAttrChange(index, 'value', e.target.value)} />
                        <button type="button" onClick={() => removeAttrRow(index)} className={styles.iconBtnDanger}><IoTrashOutline /></button>
                    </div>
                    ))}
                    <button type="button" onClick={addAttrRow} className={styles.addAttrBtn}><IoAddCircleOutline /> Thêm thuộc tính</button>
                </div>
            </div>
        </div>

        {/* CỘT PHẢI */}
        <div className={styles.rightColumn}>
            <div className={styles.card}>
                <h3>Phân loại & Giá</h3>
                <div className={styles.formGroup}>
                    <label>Danh mục</label>
                    <select name="category_id" value={product.category_id} onChange={handleTextChange} required>
                        <option value="">-- Chọn danh mục --</option>
                        {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                </div>
                
                <div className={styles.row2}>
                    <div className={styles.formGroup}>
                        <label>Giá gốc (VNĐ)</label>
                        <input type="number" name="price" value={product.price} onChange={handleTextChange} required />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Giảm giá (%)</label>
                        <input type="number" name="discount_percentage" value={product.discount_percentage} onChange={handleTextChange} min="0" max="100" />
                    </div>
                </div>

                <div className={styles.pricePreview}>
                    <span>Giá bán thực tế:</span>
                    <strong>{new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(salePrice)}</strong>
                </div>

                <div className={styles.formGroup}>
                    <label>Tồn kho</label>
                    <input type="number" name="stock_quantity" value={product.stock_quantity} onChange={handleTextChange} required min="0" />
                </div>
                
                <div className={styles.formGroup} style={{flexDirection: 'row', alignItems: 'center', gap: '10px', marginTop:'15px'}}>
                    <input type="checkbox" id="is_featured" checked={product.is_featured == 1} onChange={(e) => setProduct({...product, is_featured: e.target.checked ? 1 : 0})} style={{width: 'auto'}} />
                    <label htmlFor="is_featured" style={{margin:0, cursor:'pointer'}}>Sản phẩm nổi bật</label>
                </div>
            </div>

            {/* CARD HÌNH ẢNH */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h3>Thư viện ảnh</h3>
                    <button type="button" onClick={() => { setSelectMode('product'); setIsModalOpen(true); }} className={styles.iconBtnPrimary} title="Thêm ảnh">
                        <IoImageOutline />
                    </button>
                </div>
                
                <div className={styles.imageGrid}>
                    {productImages.length === 0 && <span className={styles.emptyState}>Chưa có ảnh</span>}
                    {productImages.map(img => (
                        <div key={img.media_file_id} className={`${styles.imageItem} ${img.is_thumbnail == 1 ? styles.isThumbnail : ''}`}>
                            <img src={`${BACKEND_URL}${img.file_path}`} alt="" />
                            <div className={styles.actionsOverlay}>
                                <button type="button" title="Đặt làm đại diện" onClick={() => handleSetThumbnail(img.media_file_id)}>★</button>
                                <button type="button" title="Xóa ảnh" onClick={() => handleUnlinkImage(img.media_file_id)}><IoTrashOutline /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      </form>

      <MediaLibraryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFileSelect={handleFileSelect}
      />
    </div>
  );
};

export default AdminProductForm;